import { useEffect, useState } from "react";
import { Combobox } from "@/components/form/Combobox";
import { Button } from "@/components/ui/button";
import { AU_STATES, COUNTRIES, DEFAULT_COUNTRY, ADDRESS_KEYS, formatAddress } from "@/lib/address";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-colors";
const AU_CODES = AU_STATES.map((s) => s.value);

// Grouped Street / Suburb / State / Postcode / Country inputs; onChange receives a partial patch
export function AddressFields({ value = {}, onChange, testid = "address", label = "Address" }) {
  const country = value.country || DEFAULT_COUNTRY;
  const isAU = country === "Australia";
  useEffect(() => { if (!value.country) onChange({ country: DEFAULT_COUNTRY }); }, [value.country]); // eslint-disable-line react-hooks/exhaustive-deps
  const setCountry = (c) => {
    const next = c || DEFAULT_COUNTRY;
    const stateOk = next === "Australia" ? AU_CODES.includes(value.state) : !AU_CODES.includes(value.state);
    onChange({ country: next, ...(stateOk ? {} : { state: null }) });
  };
  const txt = (k) => (e) => onChange({ [k]: e.target.value || null });
  return (
    <div data-testid={`${testid}-fields`}>
      {label && <label className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</label>}
      <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-2.5">
        <input className={inputCls} placeholder="Street address" value={value.street || ""} onChange={txt("street")} data-testid={`${testid}-street`} />
        <div className="grid grid-cols-6 gap-2">
          <input className={cn(inputCls, "col-span-6 sm:col-span-3")} placeholder="Suburb" value={value.suburb || ""} onChange={txt("suburb")} data-testid={`${testid}-suburb`} />
          {isAU ? (
            <Combobox className="col-span-3 sm:col-span-2" value={value.state || null} onChange={(v) => onChange({ state: v })} options={AU_STATES.map((s) => ({ ...s, display: s.value }))} placeholder="State" testid={`${testid}-state`} />
          ) : (
            <input className={cn(inputCls, "col-span-3 sm:col-span-2")} placeholder="State / Region" value={value.state || ""} onChange={txt("state")} data-testid={`${testid}-state`} />
          )}
          <input className={cn(inputCls, "col-span-3 sm:col-span-1 px-2")} placeholder="Postcode" value={value.postcode || ""} onChange={txt("postcode")} data-testid={`${testid}-postcode`} />
        </div>
        <Combobox value={country} onChange={setCountry} options={COUNTRIES} placeholder="Country" testid={`${testid}-country`} />
      </div>
    </div>
  );
}

const pickAddress = (r) => Object.fromEntries(ADDRESS_KEYS.map((k) => [k, r?.[k] ?? null]));

// Detail-page display that expands into AddressFields with Save / Cancel
export function InlineAddress({ record, onSave, testid = "inline-address" }) {
  const [draft, setDraft] = useState(null);
  const shown = formatAddress(record);
  if (!draft) {
    return (
      <button type="button" onClick={() => setDraft({ ...pickAddress(record), country: record?.country || DEFAULT_COUNTRY })} data-testid={`${testid}-display`}
        className={cn("group flex w-full items-start gap-1.5 rounded-md px-2 py-1 text-left text-sm hover:bg-slate-50", !shown && "text-slate-400")}>
        <span className="min-w-0 flex-1 whitespace-normal break-words">{shown || "Add address"}</span>
        <Pencil className="mt-1 h-3 w-3 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
      </button>
    );
  }
  return (
    <div className="space-y-2 px-1 pt-1">
      <AddressFields value={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} testid={testid} label={null} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setDraft(null)} data-testid={`${testid}-cancel`}>Cancel</Button>
        <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={async () => { await onSave(draft); setDraft(null); }} data-testid={`${testid}-save`}>Save</Button>
      </div>
    </div>
  );
}
